import React, { useMemo } from "react";
import { first, includes } from "lodash";
import Menu from "antd/lib/menu";
import Link from "@/components/Link";
import PlainButton from "@/components/plain-button/PlainButton";
import HelpTrigger from "@/components/help-trigger/HelpTrigger";
import CreateDashboardDialog from "@/components/dashboards/CreateDashboardDialog";
import { useCurrentRoute } from "@/components/application-area/Router";
import { Auth, currentUser } from "@/services/auth";
import settingsMenu from "@/services/settingsMenu";
import logoUrl from "@/assets/images/redash_icon_small.png";
import { useTheme } from "@/components/ThemeProvider";
import SunOutlined from "@ant-design/icons/SunOutlined";
import MoonOutlined from "@ant-design/icons/MoonOutlined"
import  Switch  from "antd/lib/switch";

import RobotOutlined from "@ant-design/icons/RobotOutlined";
import DatabaseOutlined from "@ant-design/icons/DatabaseOutlined";
import AreaChartOutlined from "@ant-design/icons/AreaChartOutlined";
import CodeOutlinedIcon from "@ant-design/icons/CodeOutlined";
import BellOutlined from "@ant-design/icons/BellOutlined";
import HomeOutlinedIcon from "@ant-design/icons/HomeOutlined";
import PlusOutlinedIcon from "@ant-design/icons/PlusOutlined";
import QuestionCircleOutlinedIcon from "@ant-design/icons/QuestionCircleOutlined";
import SettingOutlinedIcon from "@ant-design/icons/SettingOutlined";
import StarOutlinedIcon from "@ant-design/icons/StarOutlined";
import VersionInfo from "./VersionInfo";

import "./DesktopNavbar.less";

function NavbarSection({ children, ...props }) {
  return (
    <Menu selectable={false} mode="vertical" theme="dark" {...props}>
      {children}
    </Menu>
  );
}

function useNavbarActiveState() {
  const currentRoute = useCurrentRoute();

  return useMemo(
    () => ({
      dashboards: includes(
        [
          "Dashboards.List",
          "Dashboards.Favorites",
          "Dashboards.My",
          "Dashboards.ViewOrEdit",
          "Dashboards.LegacyViewOrEdit",
        ],
        currentRoute.id
      ),
      queries: includes(
        [
          "Queries.List",
          "Queries.Favorites",
          "Queries.Archived",
          "Queries.My",
          "Queries.View",
          "Queries.New",
          "Queries.Edit",
        ],
        currentRoute.id
      ),
      dataSources: includes(["DataSources.List"], currentRoute.id),
      alerts: includes(
        [
          "Alerts.List", 
          "Alerts.New",
          "Alerts.View",
          "Alerts.Overview",
          "Alerts.Edit",
          "Alerts.Fevents",
          "Alerts.Stats",
          "AlertEvents.List"
        ], 
        currentRoute.id
      ),
      indexers: includes(
        [
          "Indexers.List",
          "Indexers.New",
          "Indexers.View",
          "Indexers.Overview",
          "Indexers.Edit"
        ],
        currentRoute.id
      ),
      mlModels: includes(
        [
          "MLModels.List", 
          "MLModels.New",
          "MLModels.View",
          "MLModels.Overview",
          "MLModels.Predictions",
          "MLModels.Versions",
          "MLModels.Edit",
          "MLModels.Metrics",
          "MLModels.MetricsHistory",
          "MLModels.Favorites",
          "MLModels.Archived",
          "MLModels.My",
          
          
          "MLModelsVersions.List",
          "MLModelsVersions.View",
          "MLModelsVersions.Favorites",
          "MLModelsVersions.Archived",
          "MLModelsVersions.My",

          "PredictionResult.View",
          "PredictionResults.List",
          "PredictionResult.View",
          "PredictionResults.Favorites",
          "PredictionResults.Archived",
          "PredictionResults.My",
        ], 
        currentRoute.id
      ),

    }),
    [currentRoute.id]
  );
}

export default function DesktopNavbar() {
  const firstSettingsTab = first(settingsMenu.getAvailableItems());

  const activeState = useNavbarActiveState();

  const canCreateQuery = currentUser.hasPermission("create_query");
  const canCreateDashboard = currentUser.hasPermission("create_dashboard");
  const canCreateAlert = currentUser.hasPermission("list_alerts");
  const canCreateModel = currentUser.hasPermission("list_models");
  const canCreateIndexer = currentUser.hasPermission("list_indexers");
  const currentRoute = useCurrentRoute();
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <nav className={`desktop-navbar ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <NavbarSection className={"desktop-navbar-logo"}>
      <Menu.Item key="dashboards" className={currentRoute.routeParams['0'] === "-home" ? "navbar-active-item" : null}>
        <div role="menuitem">
          <Link href="./dashboards/1-home">
            <img src={logoUrl} alt="Inverse Watch" />
          </Link>
        </div>
      </Menu.Item>
      </NavbarSection>

      <NavbarSection>
        {currentUser.hasPermission("list_dashboards") && currentUser.hasPermission("view_query") && (
        <Menu.Item key="favorites" className={currentRoute.path === "/favorites"? "navbar-active-item" : null}>
          <Link href="./favorites">
            <StarOutlinedIcon aria-label="Favorites navigation button" />
            <span className="desktop-navbar-label">Favorites</span>
          </Link>
        </Menu.Item>
        )}
        {currentUser.hasPermission("list_dashboards") && (
          <Menu.Item key="dashboards" className={activeState.dashboards && currentRoute.routeParams['0'] !== "-home" ? "navbar-active-item" : null}>
            <Link href="dashboards">
              <AreaChartOutlined aria-label="Dashboard navigation button" />
              <span className="desktop-navbar-label">Dashboards</span>
            </Link>
          </Menu.Item>
        )}
      </NavbarSection>
      <NavbarSection>
        {currentUser.hasPermission("view_query") && (
          <Menu.Item key="queries" className={activeState.queries ? "navbar-active-item" : null}>
            <Link href="queries">
              <CodeOutlinedIcon aria-label="Queries navigation button" />
              <span className="desktop-navbar-label">Queries</span>
            </Link>
          </Menu.Item>
        )}
      </NavbarSection>
      <NavbarSection>
        {currentUser.hasPermission("list_alerts") && (
          <Menu.SubMenu
            key="alerts"
            className={activeState.alerts || activeState.alertevent ? "navbar-active-item" : null}
            title={
              <Link href="alerts" className="navbar-submenu-title">
                <BellOutlined aria-label="Alerts navigation button" />
                <span className="desktop-navbar-label">Alerts</span>
              </Link>
            }
          >
            <Menu.Item key="alerts-list">
              <Link href="alerts">
                Alerts
              </Link>
            </Menu.Item>
            <Menu.Item key="alerts-history">
              <Link href="alert_events">
                Alerts History
              </Link>
            </Menu.Item>
          </Menu.SubMenu>
        )}
      </NavbarSection>
      <NavbarSection>
        {currentUser.hasPermission("list_indexers") && (
          <Menu.Item key="indexers" className={activeState.indexers ? "navbar-active-item" : null}>
            <Link href="indexers">
              <DatabaseOutlined aria-label="Indexers navigation button" />
              <span className="desktop-navbar-label">Indexers</span>
            </Link>
          </Menu.Item>
        )}
      </NavbarSection>
      
      <NavbarSection>
        {currentUser.hasPermission("list_models") && (
          <Menu.SubMenu
            key="ml_models"
            className={activeState.mlModels ? "navbar-active-item" : null}
            title={
              <Link href="ml_models" className="navbar-submenu-title">
                <RobotOutlined aria-label="Models navigation button" />
                <span className="desktop-navbar-label">Models</span>
              </Link>
            }
          >
            <Menu.Item key="models-list">
              <Link href="ml_models">
                Models
              </Link>
            </Menu.Item>
            <Menu.Item key="models-versions-list">
              <Link href="ml_models_versions">
                Versions
              </Link>
            </Menu.Item>
            <Menu.Item key="predictions-list">
              <Link href="predictions">
                Predictions
              </Link>
            </Menu.Item>
          </Menu.SubMenu>
        )}
      </NavbarSection>

      <NavbarSection className="desktop-navbar-spacer">
        {(canCreateQuery || canCreateDashboard || canCreateAlert || canCreateIndexer) && (
          <Menu.SubMenu
            key="create"
            popupClassName="desktop-navbar-submenu"
            data-test="CreateButton"
            tabIndex={0}
            title={
              <React.Fragment>
                <PlusOutlinedIcon />
                <span className="desktop-navbar-label">Create</span>
              </React.Fragment>
            }>
            {canCreateQuery && (
              <Menu.Item key="new-query">
                <Link href="queries/new" data-test="CreateQueryMenuItem">
                  New Query
                </Link>
              </Menu.Item>
            )}
            {canCreateDashboard && (
              <Menu.Item key="new-dashboard">
                <PlainButton data-test="CreateDashboardMenuItem" onClick={() => CreateDashboardDialog.showModal()}>
                  New Dashboard
                </PlainButton>
              </Menu.Item>
            )}
            {canCreateAlert && (
              <Menu.Item key="new-alert">
                <Link data-test="CreateAlertMenuItem" href="alerts/new">
                  New Alert
                </Link>
              </Menu.Item>
            )}
            {canCreateIndexer && (
              <Menu.Item key="new-indexer">
                <Link data-test="CreateIndexerMenuItem" href="indexers/new">
                  New Indexer
                </Link>
              </Menu.Item>
            )}
            {canCreateModel && (
              <Menu.Item key="new-model">
                <Link data-test="CreateAlertMenuItem" href="ml_models/new">
                  New Model
                </Link>
              </Menu.Item>
            )}
          </Menu.SubMenu>
        )}
      </NavbarSection>

      <NavbarSection>
        <Menu.Item key="help">
          <HelpTrigger showTooltip={false} type="HOME" tabIndex={0}>
            <QuestionCircleOutlinedIcon />
            <span className="desktop-navbar-label">Docs</span>
          </HelpTrigger>
        </Menu.Item>
        <Menu.Item key="landing">
          <Link href="https://inverse.watch" target="_blank" rel="noopener noreferrer">
            <HomeOutlinedIcon />
            <span className="desktop-navbar-label">Landing</span>
          </Link>
        </Menu.Item>
        {firstSettingsTab && (
          <Menu.Item key="settings" className={activeState.dataSources ? "navbar-active-item" : null}>
            <Link href={firstSettingsTab.path} data-test="SettingsLink">
              <SettingOutlinedIcon />
              <span className="desktop-navbar-label">Settings</span>
            </Link>
          </Menu.Item>
        )}
      </NavbarSection>

      <NavbarSection className="desktop-navbar-profile-menu">
        <Menu.SubMenu
          key="profile"
          popupClassName="desktop-navbar-submenu"
          tabIndex={0}
          title={
            <span data-test="ProfileDropdown" className="desktop-navbar-profile-menu-title">
              <img className="profile__image_thumb" src={currentUser.profile_image_url} alt={currentUser.name} />
            </span>
          }>
          <Menu.Item key="profile">
            <Link href="users/me">Profile</Link>
          </Menu.Item>
          {currentUser.hasPermission("super_admin") && (
            <Menu.Item key="status">
              <Link href="admin/status">System Status</Link>
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item key="logout">
            <PlainButton data-test="LogOutButton" onClick={() => Auth.logout()}>
              Log out
            </PlainButton>
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item key="version" role="presentation" disabled className="version-info">
            <VersionInfo />
          </Menu.Item>
        </Menu.SubMenu>
      </NavbarSection>

      <NavbarSection>
        <Menu.Item key="theme-toggle">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Switch
              checked={isDarkMode}
              onChange={toggleTheme}
              checkedChildren={<MoonOutlined style={{ fontSize: '10px' }} />}
              unCheckedChildren={<SunOutlined style={{ fontSize: '10px' }} />}
              size="small"
            />
          </div>
        </Menu.Item>
      </NavbarSection>
    </nav>
  );
}
